import {
    Badge,
    Box,
    Button,
    Checkbox,
    CloseButton,
    Divider,
    Drawer,
    Fieldset,
    Flex,
    Group,
    Loader,
    MultiSelect,
    NumberInput,
    Overlay,
    Pagination,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
} from '@mantine/core'
import {IconChevronDown, IconChevronUp} from '@tabler/icons-react'
import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {useCallback, useMemo, useState} from 'react'

import {
    getOrganization,
    getOrganizationProspects,
    getProspectDetail,
    type GetProspectsOptions,
} from 'src/api/client/api'
import type {ProspectsResponse} from 'src/api/client/models/prospect'
import {PageHeader} from 'src/components/page-header'
import {ProspectDetailModal} from 'src/components/prospect-detail-modal/prospect-detail-modal'
import {useScenarioPlanning} from 'src/components/scenario/scenario-context'
import {SectionCard} from 'src/components/section-card'
import {engagementBadgeColor} from 'src/lib/engagement-colors'
import {formatCurrency} from 'src/lib/format'

const CAPACITY_BUCKETS = [
    '$1,000,000+',
    '$500,000 to $999,999',
    '$250,000 to $499,999',
    '$100,000 to $249,999',
    '$50,000 to $99,999',
    '$25,000 to $49,999',
    '$10,000 to $24,999',
    '$5,000 to $9,999',
    'Under $5,000',
]

const ENGAGEMENT_LEVELS = [
    'Very Engaged',
    'Engaged',
    'Somewhat Engaged',
    'Minimally Engaged',
    'Disengaged',
]

const SORT_COLUMNS = [
    {key: 'last_name', label: 'Name'},
    {key: 'engagement_level', label: 'Engagement'},
    {key: 'estimated_capacity', label: 'Est. Capacity'},
    {key: 'total_gift_amount', label: 'Lifetime Giving'},
    {key: 'manager_name', label: 'Manager'},
] as const

type SortColumnKey = (typeof SORT_COLUMNS)[number]['key']
type ProspectListRow = ProspectsResponse['items'][number]

function toArray(val: unknown): string[] | undefined {
    if (val == null) return undefined
    if (Array.isArray(val)) return val.filter((v): v is string => typeof v === 'string')
    if (typeof val === 'string') return val ? [val] : undefined
    return undefined
}

function searchBooleanTrue(val: unknown): true | undefined {
    if (val === true || val === 'true') return true
    return undefined
}

type ProspectSearchParams = {
    page?: number
    manager?: string
    capacityBuckets?: string[]
    engagementLevels?: string[]
    totalGiftsMin?: number
    totalGiftsMax?: number
    hasPlannedGivingOpportunity?: true
    secInsider?: true
    foundation?: true
    yearsSinceLastGiftMin?: number
    yearsSinceLastGiftMax?: number
    sortBy?: string
    sortDir?: 'asc' | 'desc'
    prospectId?: string
}

function renderProspectCell(row: ProspectListRow, key: SortColumnKey) {
    switch (key) {
        case 'last_name':
            return [row.firstName, row.lastName].filter(Boolean).join(' ') || '—'
        case 'engagement_level':
            return row.engagementLevel ? (
                <Badge color={engagementBadgeColor(row.engagementLevel)}>
                    {row.engagementLevel}
                </Badge>
            ) : (
                '—'
            )
        case 'estimated_capacity':
            return row.estimatedCapacity != null ? formatCurrency(row.estimatedCapacity) : '—'
        case 'total_gift_amount':
            return row.totalGiftAmount != null ? formatCurrency(row.totalGiftAmount) : '—'
        case 'manager_name':
            return row.managerName ?? '—'
    }
}

export const Route = createFileRoute('/_authed/organizations/$orgId/prospects')({
    component: ProspectsPage,
    validateSearch: (search: Record<string, unknown>): ProspectSearchParams => {
        const capacityBuckets = toArray(search['capacityBuckets'])
        const engagementLevels = toArray(search['engagementLevels'])
        const totalGiftsMin =
            typeof search['totalGiftsMin'] === 'number'
                ? search['totalGiftsMin']
                : typeof search['totalGiftsMin'] === 'string'
                  ? Number(search['totalGiftsMin']) || undefined
                  : undefined
        const totalGiftsMax =
            typeof search['totalGiftsMax'] === 'number'
                ? search['totalGiftsMax']
                : typeof search['totalGiftsMax'] === 'string'
                  ? Number(search['totalGiftsMax']) || undefined
                  : undefined
        const yearsSinceLastGiftMin =
            typeof search['yearsSinceLastGiftMin'] === 'number'
                ? search['yearsSinceLastGiftMin']
                : typeof search['yearsSinceLastGiftMin'] === 'string'
                  ? Number(search['yearsSinceLastGiftMin']) || undefined
                  : undefined
        const yearsSinceLastGiftMax =
            typeof search['yearsSinceLastGiftMax'] === 'number'
                ? search['yearsSinceLastGiftMax']
                : typeof search['yearsSinceLastGiftMax'] === 'string'
                  ? Number(search['yearsSinceLastGiftMax']) || undefined
                  : undefined
        return {
            page: Number(search['page']) || 1,
            manager: search['manager'] as string | undefined,
            capacityBuckets: capacityBuckets?.length ? capacityBuckets : undefined,
            engagementLevels: engagementLevels?.length ? engagementLevels : undefined,
            totalGiftsMin:
                totalGiftsMin != null && !Number.isNaN(totalGiftsMin) ? totalGiftsMin : undefined,
            totalGiftsMax:
                totalGiftsMax != null && !Number.isNaN(totalGiftsMax) ? totalGiftsMax : undefined,
            hasPlannedGivingOpportunity: searchBooleanTrue(search['hasPlannedGivingOpportunity']),
            secInsider: searchBooleanTrue(search['secInsider']),
            foundation: searchBooleanTrue(search['foundation']),
            yearsSinceLastGiftMin:
                yearsSinceLastGiftMin != null && !Number.isNaN(yearsSinceLastGiftMin)
                    ? yearsSinceLastGiftMin
                    : undefined,
            yearsSinceLastGiftMax:
                yearsSinceLastGiftMax != null && !Number.isNaN(yearsSinceLastGiftMax)
                    ? yearsSinceLastGiftMax
                    : undefined,
            sortBy: (search['sortBy'] as string) || 'last_name',
            sortDir:
                search['sortDir'] === 'asc' || search['sortDir'] === 'desc'
                    ? search['sortDir']
                    : 'asc',
            prospectId: typeof search['prospectId'] === 'string' ? search['prospectId'] : undefined,
        }
    },
})

const ALL_MANAGERS = ''
const UNMANAGED = '__unmanaged__'

function ProspectsPage() {
    const {orgId} = Route.useParams()
    const search = Route.useSearch()
    const navigate = useNavigate()
    const {appliedScenario} = useScenarioPlanning()
    const [moreFiltersOpen, setMoreFiltersOpen] = useState(false)

    const {
        page = 1,
        manager,
        capacityBuckets,
        engagementLevels,
        totalGiftsMin,
        totalGiftsMax,
        hasPlannedGivingOpportunity,
        secInsider,
        foundation,
        yearsSinceLastGiftMin,
        yearsSinceLastGiftMax,
        sortBy = 'last_name',
        sortDir = 'asc',
        prospectId,
    } = search

    const options: GetProspectsOptions = {
        page,
        pageSize: 25,
        sortBy,
        sortDir,
        filters: {
            manager: manager || undefined,
            capacityBuckets,
            engagementLevels,
            totalGiftsMin,
            totalGiftsMax,
            hasPlannedGivingOpportunity,
            secInsider,
            foundation,
            yearsSinceLastGiftMin,
            yearsSinceLastGiftMax,
        },
    }

    const {data: org} = useQuery({
        queryKey: ['organization', orgId],
        queryFn: () => getOrganization(orgId),
    })

    const {data, isLoading, isFetching, error} = useQuery({
        queryKey: ['prospects', orgId, options, appliedScenario],
        queryFn: () => getOrganizationProspects(orgId, options, appliedScenario),
        placeholderData: keepPreviousData,
    })

    const {data: prospectDetail, isLoading: isLoadingDetail} = useQuery({
        queryKey: ['prospect-detail', orgId, prospectId, appliedScenario],
        queryFn: () => getProspectDetail(orgId, prospectId!, appliedScenario),
        enabled: !!prospectId,
    })

    const managers = org?.managers ?? []
    const visibleSortColumns = SORT_COLUMNS.filter(
        (col) => col.key !== 'manager_name' || managers.length > 0,
    )
    const managerOptions = [
        {value: ALL_MANAGERS, label: 'All Managers'},
        {value: UNMANAGED, label: 'Unmanaged'},
        ...managers.map((m) => ({value: m, label: m})),
    ]

    const totalPages = data ? Math.ceil(data.total / data.pageSize) || 1 : 1

    const updateSearch = useCallback(
        (updates: Partial<ProspectSearchParams>) => {
            const merged: Record<string, unknown> = {...search, ...updates}
            const cleaned: Record<string, unknown> = {}
            for (const [key, value] of Object.entries(merged)) {
                if (value === undefined) continue
                if (Array.isArray(value) && value.length === 0) continue
                cleaned[key] = value
            }
            navigate({
                to: '.',
                search: cleaned,
                replace: true,
            })
        },
        [navigate, search],
    )

    const secondaryFilterCount = useMemo(() => {
        let n = 0
        if (totalGiftsMin != null) n += 1
        if (totalGiftsMax != null) n += 1
        if (hasPlannedGivingOpportunity === true) n += 1
        if (secInsider === true) n += 1
        if (foundation === true) n += 1
        if (yearsSinceLastGiftMin != null) n += 1
        if (yearsSinceLastGiftMax != null) n += 1
        return n
    }, [
        totalGiftsMin,
        totalGiftsMax,
        hasPlannedGivingOpportunity,
        secInsider,
        foundation,
        yearsSinceLastGiftMin,
        yearsSinceLastGiftMax,
    ])

    const filterChips = useMemo(() => {
        type Chip = {id: string; label: string; onRemove: () => void}
        const chips: Chip[] = []

        if (manager) {
            chips.push({
                id: 'manager',
                label: manager === UNMANAGED ? 'Manager: Unmanaged' : `Manager: ${manager}`,
                onRemove: () => updateSearch({manager: undefined, page: 1}),
            })
        }

        const cap = capacityBuckets ?? []
        for (const bucket of cap) {
            chips.push({
                id: `capacity:${bucket}`,
                label: `Capacity: ${bucket}`,
                onRemove: () => {
                    const next = cap.filter((b) => b !== bucket)
                    updateSearch({
                        capacityBuckets: next.length ? next : undefined,
                        page: 1,
                    })
                },
            })
        }

        const eng = engagementLevels ?? []
        for (const level of eng) {
            chips.push({
                id: `engagement:${level}`,
                label: `Engagement: ${level}`,
                onRemove: () => {
                    const next = eng.filter((l) => l !== level)
                    updateSearch({
                        engagementLevels: next.length ? next : undefined,
                        page: 1,
                    })
                },
            })
        }

        if (totalGiftsMin != null || totalGiftsMax != null) {
            let giftsLabel = 'Total gifts'
            if (totalGiftsMin != null && totalGiftsMax != null) {
                giftsLabel = `Total gifts: ${formatCurrency(totalGiftsMin)} – ${formatCurrency(totalGiftsMax)}`
            } else if (totalGiftsMin != null) {
                giftsLabel = `Total gifts ≥ ${formatCurrency(totalGiftsMin)}`
            } else if (totalGiftsMax != null) {
                giftsLabel = `Total gifts ≤ ${formatCurrency(totalGiftsMax)}`
            }
            chips.push({
                id: 'totalGifts',
                label: giftsLabel,
                onRemove: () =>
                    updateSearch({
                        totalGiftsMin: undefined,
                        totalGiftsMax: undefined,
                        page: 1,
                    }),
            })
        }

        if (hasPlannedGivingOpportunity === true) {
            chips.push({
                id: 'plannedGiving',
                label: 'Planned giving potential',
                onRemove: () => updateSearch({hasPlannedGivingOpportunity: undefined, page: 1}),
            })
        }

        if (secInsider === true) {
            chips.push({
                id: 'secInsider',
                label: 'SEC insider',
                onRemove: () => updateSearch({secInsider: undefined, page: 1}),
            })
        }

        if (foundation === true) {
            chips.push({
                id: 'foundation',
                label: 'Foundation',
                onRemove: () => updateSearch({foundation: undefined, page: 1}),
            })
        }

        if (yearsSinceLastGiftMin != null || yearsSinceLastGiftMax != null) {
            let yLabel = 'Years since last gift'
            if (yearsSinceLastGiftMin != null && yearsSinceLastGiftMax != null) {
                yLabel = `Years since last gift: ${yearsSinceLastGiftMin}–${yearsSinceLastGiftMax}`
            } else if (yearsSinceLastGiftMin != null) {
                yLabel = `Years since last gift ≥ ${yearsSinceLastGiftMin}`
            } else if (yearsSinceLastGiftMax != null) {
                yLabel = `Years since last gift ≤ ${yearsSinceLastGiftMax}`
            }
            chips.push({
                id: 'yearsSinceGift',
                label: yLabel,
                onRemove: () =>
                    updateSearch({
                        yearsSinceLastGiftMin: undefined,
                        yearsSinceLastGiftMax: undefined,
                        page: 1,
                    }),
            })
        }

        return chips
    }, [
        manager,
        capacityBuckets,
        engagementLevels,
        totalGiftsMin,
        totalGiftsMax,
        hasPlannedGivingOpportunity,
        secInsider,
        foundation,
        yearsSinceLastGiftMin,
        yearsSinceLastGiftMax,
        updateSearch,
    ])

    const clearAllFilters = useCallback(() => {
        updateSearch({
            page: 1,
            manager: undefined,
            capacityBuckets: undefined,
            engagementLevels: undefined,
            totalGiftsMin: undefined,
            totalGiftsMax: undefined,
            hasPlannedGivingOpportunity: undefined,
            secInsider: undefined,
            foundation: undefined,
            yearsSinceLastGiftMin: undefined,
            yearsSinceLastGiftMax: undefined,
        })
    }, [updateSearch])

    const clearSecondaryFilters = useCallback(() => {
        updateSearch({
            page: 1,
            totalGiftsMin: undefined,
            totalGiftsMax: undefined,
            hasPlannedGivingOpportunity: undefined,
            secInsider: undefined,
            foundation: undefined,
            yearsSinceLastGiftMin: undefined,
            yearsSinceLastGiftMax: undefined,
        })
    }, [updateSearch])

    const handleSort = (column: string) => {
        if (sortBy === column) {
            updateSearch({sortDir: sortDir === 'asc' ? 'desc' : 'asc'})
        } else {
            updateSearch({sortBy: column, sortDir: 'asc'})
        }
    }

    return (
        <Stack>
            <PageHeader
                title="Prospects"
                description="Browse and filter individual prospect records."
            />
            <SectionCard title="Prospects">
                <Paper bg="gray.0" p="md" radius="sm" mb="md">
                    <Stack gap="sm">
                        <Flex gap="md" wrap="wrap" align="flex-end">
                            {managers.length > 0 && (
                                <Select
                                    label="Manager"
                                    data={managerOptions}
                                    value={manager ?? ALL_MANAGERS}
                                    onChange={(v) =>
                                        updateSearch({
                                            manager:
                                                v === ALL_MANAGERS ? undefined : (v ?? undefined),
                                            page: 1,
                                        })
                                    }
                                    clearable={false}
                                    size="sm"
                                    w={200}
                                />
                            )}
                            <MultiSelect
                                label="Capacity"
                                data={CAPACITY_BUCKETS}
                                value={capacityBuckets ?? []}
                                onChange={(v) =>
                                    updateSearch({
                                        capacityBuckets: v.length ? v : undefined,
                                        page: 1,
                                    })
                                }
                                placeholder={(capacityBuckets?.length ?? 0) > 0 ? '' : 'All'}
                                clearable
                                size="sm"
                                w={220}
                            />
                            <MultiSelect
                                label="Engagement"
                                data={ENGAGEMENT_LEVELS}
                                value={engagementLevels ?? []}
                                onChange={(v) =>
                                    updateSearch({
                                        engagementLevels: v.length ? v : undefined,
                                        page: 1,
                                    })
                                }
                                placeholder={(engagementLevels?.length ?? 0) > 0 ? '' : 'All'}
                                clearable
                                size="sm"
                                w={200}
                            />
                            <Button
                                variant="default"
                                size="sm"
                                aria-haspopup="dialog"
                                aria-expanded={moreFiltersOpen}
                                onClick={() => setMoreFiltersOpen(true)}
                                ml="auto"
                                rightSection={
                                    secondaryFilterCount > 0 ? (
                                        <Badge size="xs" variant="filled" circle>
                                            {secondaryFilterCount}
                                        </Badge>
                                    ) : null
                                }
                            >
                                « More filters
                            </Button>
                        </Flex>
                        {filterChips.length > 0 && (
                            <>
                                <Divider />
                                <Group gap="xs" align="center" wrap="wrap">
                                    <Group gap="xs" wrap="wrap" style={{flex: 1}}>
                                        {filterChips.map((chip) => (
                                            <Badge
                                                key={chip.id}
                                                variant="light"
                                                size="lg"
                                                pr={3}
                                                rightSection={
                                                    <CloseButton
                                                        size="xs"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={chip.onRemove}
                                                        aria-label={`Remove ${chip.label} filter`}
                                                    />
                                                }
                                            >
                                                {chip.label}
                                            </Badge>
                                        ))}
                                    </Group>
                                    {filterChips.length >= 2 && (
                                        <Button
                                            variant="subtle"
                                            size="xs"
                                            onClick={clearAllFilters}
                                        >
                                            Clear all
                                        </Button>
                                    )}
                                </Group>
                            </>
                        )}
                    </Stack>
                </Paper>
                <Drawer
                    opened={moreFiltersOpen}
                    onClose={() => setMoreFiltersOpen(false)}
                    position="right"
                    size="md"
                    title={
                        <Text fw={600} size="md">
                            More filters
                        </Text>
                    }
                >
                    <Stack gap="lg">
                        <Fieldset legend="Total gifts">
                            <SimpleGrid cols={2} spacing="sm">
                                <NumberInput
                                    label="Min"
                                    value={totalGiftsMin ?? ''}
                                    onChange={(v) =>
                                        updateSearch({
                                            totalGiftsMin:
                                                v !== '' && typeof v === 'number' ? v : undefined,
                                            page: 1,
                                        })
                                    }
                                    min={0}
                                    decimalScale={0}
                                    size="sm"
                                    thousandSeparator=","
                                    prefix="$"
                                />
                                <NumberInput
                                    label="Max"
                                    value={totalGiftsMax ?? ''}
                                    onChange={(v) =>
                                        updateSearch({
                                            totalGiftsMax:
                                                v !== '' && typeof v === 'number' ? v : undefined,
                                            page: 1,
                                        })
                                    }
                                    min={0}
                                    decimalScale={0}
                                    size="sm"
                                    thousandSeparator=","
                                    prefix="$"
                                />
                            </SimpleGrid>
                        </Fieldset>
                        <Fieldset legend="Years since last gift">
                            <SimpleGrid cols={2} spacing="sm">
                                <NumberInput
                                    label="Min"
                                    value={yearsSinceLastGiftMin ?? ''}
                                    onChange={(v) =>
                                        updateSearch({
                                            yearsSinceLastGiftMin:
                                                v !== '' && typeof v === 'number' ? v : undefined,
                                            page: 1,
                                        })
                                    }
                                    min={0}
                                    decimalScale={0}
                                    size="sm"
                                />
                                <NumberInput
                                    label="Max"
                                    value={yearsSinceLastGiftMax ?? ''}
                                    onChange={(v) =>
                                        updateSearch({
                                            yearsSinceLastGiftMax:
                                                v !== '' && typeof v === 'number' ? v : undefined,
                                            page: 1,
                                        })
                                    }
                                    min={0}
                                    decimalScale={0}
                                    size="sm"
                                />
                            </SimpleGrid>
                        </Fieldset>
                        <Fieldset legend="Attributes">
                            <Stack gap="sm">
                                <Checkbox
                                    label="Planned giving potential"
                                    description="Prospects likely to make a planned gift"
                                    checked={hasPlannedGivingOpportunity === true}
                                    onChange={(e) =>
                                        updateSearch({
                                            hasPlannedGivingOpportunity: e.currentTarget.checked
                                                ? true
                                                : undefined,
                                            page: 1,
                                        })
                                    }
                                    size="sm"
                                />
                                <Checkbox
                                    label="SEC insider"
                                    description="Prospects likely to be insiders at public companies"
                                    checked={secInsider === true}
                                    onChange={(e) =>
                                        updateSearch({
                                            secInsider: e.currentTarget.checked ? true : undefined,
                                            page: 1,
                                        })
                                    }
                                    size="sm"
                                />
                                <Checkbox
                                    label="Foundation"
                                    description="Prospects likely to be involved in a foundation"
                                    checked={foundation === true}
                                    onChange={(e) =>
                                        updateSearch({
                                            foundation: e.currentTarget.checked ? true : undefined,
                                            page: 1,
                                        })
                                    }
                                    size="sm"
                                />
                            </Stack>
                        </Fieldset>
                        <Group justify="space-between">
                            <Button
                                variant="subtle"
                                onClick={clearSecondaryFilters}
                                disabled={secondaryFilterCount === 0}
                            >
                                Clear filters
                            </Button>
                            <Button onClick={() => setMoreFiltersOpen(false)}>Done</Button>
                        </Group>
                    </Stack>
                </Drawer>
                {isLoading ? (
                    <Loader size="md" role="progressbar" />
                ) : error && !data ? (
                    <Text size="sm" c="red">
                        Error loading prospects: {error.message}
                    </Text>
                ) : data ? (
                    <Box pos="relative" aria-busy={isFetching}>
                        <Text size="sm" mb="sm" fw={500}>
                            Total prospect{data.total === 1 ? '' : 's'}: {data.total}
                        </Text>
                        <Table layout="fixed" style={{minWidth: 700}} striped highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    {visibleSortColumns.map(({key, label}) => (
                                        <Table.Th
                                            key={key}
                                            style={{
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                ...(key === 'estimated_capacity' ||
                                                key === 'total_gift_amount'
                                                    ? {textAlign: 'right'}
                                                    : {}),
                                            }}
                                            onClick={() => handleSort(key)}
                                        >
                                            <Group
                                                gap={4}
                                                justify={
                                                    key === 'estimated_capacity' ||
                                                    key === 'total_gift_amount'
                                                        ? 'flex-end'
                                                        : 'flex-start'
                                                }
                                            >
                                                {label}
                                                {sortBy === key &&
                                                    (sortDir === 'asc' ? (
                                                        <IconChevronUp
                                                            size={14}
                                                            style={{
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ) : (
                                                        <IconChevronDown
                                                            size={14}
                                                            style={{
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ))}
                                            </Group>
                                        </Table.Th>
                                    ))}
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {data.total === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={visibleSortColumns.length}>
                                            <Text
                                                role="status"
                                                size="md"
                                                c="dimmed"
                                                p="sm"
                                                style={{textAlign: 'center'}}
                                            >
                                                No prospects to display. Try adjusting filters or
                                                check back later.
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    (data.items ?? []).map((r) => (
                                        <Table.Tr
                                            key={r.id}
                                            onClick={() => updateSearch({prospectId: r.id})}
                                            style={{cursor: 'pointer'}}
                                        >
                                            {visibleSortColumns.map(({key}) => (
                                                <Table.Td
                                                    key={key}
                                                    style={
                                                        key === 'estimated_capacity' ||
                                                        key === 'total_gift_amount'
                                                            ? {textAlign: 'right'}
                                                            : undefined
                                                    }
                                                >
                                                    {renderProspectCell(r, key)}
                                                </Table.Td>
                                            ))}
                                        </Table.Tr>
                                    ))
                                )}
                            </Table.Tbody>
                        </Table>
                        {totalPages > 1 && (
                            <Pagination
                                total={totalPages}
                                value={data.page}
                                onChange={(p) => updateSearch({page: p})}
                                mt="md"
                            />
                        )}
                        {isFetching && (
                            <Overlay backgroundOpacity={0.08} center zIndex={1}>
                                <Loader size="md" role="progressbar" />
                            </Overlay>
                        )}
                    </Box>
                ) : null}
                {isLoadingDetail && <Loader size="md" role="progressbar" />}
            </SectionCard>
            {prospectDetail && (
                <ProspectDetailModal
                    prospect={prospectDetail}
                    opened={!!prospectId}
                    onClose={() => updateSearch({prospectId: undefined})}
                />
            )}
        </Stack>
    )
}
